import React, { type FC, useMemo } from "react";
import { Modal as SemanticModal } from "semantic-ui-react";

import styles from "./wizardModal.module.scss";
import ModalHeader from "../../modal/header/ModalHeader";
import { Button } from "../../buttons/button/Button";
import StepLabelsList from "../StepLabelsList";
import { useWizard } from "../../../hooks/useWizard";
import type { SectionProps } from "../wizardNewPropTypes";
import { type WizardStepHandlers } from "../../../interfaces";

export interface WizardModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onFinish: () => void;
  children: (React.ReactElement<SectionProps> | null | false | undefined)[];
  stepActions: Record<number, WizardStepHandlers>;
  wizardDescription?: React.ReactNode;
  activeStepIndex?: number;
}
const WizardModal: FC<WizardModalProps> = (props: WizardModalProps) => {
  const {
    closeHandler,
    onStepSelected,
    onNext,
    onPrev,
    availableStepIndexes,
    activeStep,
    activeIndex,
    filteredChildren,
    onFinish,
  } = useWizard({
    children: props.children,
    onCancel: props.onClose,
    onProgress: (activeIndex: number) => props.stepActions[activeIndex]?.onNext?.(),
    onRegress: (activeIndex: number) => props.stepActions[activeIndex]?.onPrevious?.(),
    onFinish: props.onFinish,
    isSaveInProgress: false,
    activeStepIndex: props.activeStepIndex,
  });

  const isLastStep = filteredChildren.length === activeIndex + 1;
  const preRenderedSteps = useMemo(() => filteredChildren.filter((c) => c.props?.preRender), [filteredChildren]);

  return (
    <SemanticModal role="dialog" open={props.isOpen} size="large" className={styles.root}>
      <SemanticModal.Header>
        <ModalHeader title={props.title} onClose={closeHandler} />
        <div className={styles.listWrapper}>
          <StepLabelsList
            steps={filteredChildren}
            activeIndex={activeIndex}
            onStepSelected={onStepSelected}
            availableStepIndexes={availableStepIndexes}
          />

          {props.wizardDescription}
        </div>
      </SemanticModal.Header>
      <SemanticModal.Content className={styles.content}>
        <>
          {!activeStep.props.preRender && filteredChildren[activeIndex]}
          {preRenderedSteps.map((step, index) => (
            <div key={index} hidden={step !== activeStep}>
              {step}
            </div>
          ))}
        </>
      </SemanticModal.Content>
      <SemanticModal.Actions>
        {activeStep.props.extraAction}
        <Button blur primary className="previous control" disabled={activeIndex === 0} onClick={onPrev}>
          Previous
        </Button>
        <Button
          blur
          primary
          className="finish control"
          onClick={isLastStep ? onFinish : onNext}
          disabled={activeStep.props.isLocked}
        >
          {isLastStep ? activeStep.props.finishButton || "Finish" : "Next"}
        </Button>
      </SemanticModal.Actions>
    </SemanticModal>
  );
};

export default WizardModal;
